import React, { useMemo } from 'react';
import { Text, View, Image } from '@react-pdf/renderer';
import { pdfSharedStyles } from '../../../pdfSharedStyles';
import { TFunction } from 'i18next';
import { unit as unitEnum } from '../../../../../enum/project';
import { convertFieldValue } from '../../../../../services/UnitConversionService';
import { ValueUnitCells } from '../../shared/pdfUnitMerge';
import { parseHtmlToPdfText } from '../../shared/pdfHtmlText';

interface ContainerYardResultsCoreProps {
    output: any;
    projectData: any;
    tireImageUnreinforced?: string;
    tireImageReinforced?: string;
    chartImage?: string;
    t: TFunction;
    showInputData?: boolean;
    showChart?: boolean;
    showResults?: boolean;
    customTitle?: string;
}

export const ContainerYardResultsCore: React.FC<ContainerYardResultsCoreProps> = ({
    output,
    projectData,
    tireImageUnreinforced,
    tireImageReinforced,
    chartImage,
    t,
    showInputData = true,
    showChart = true,
    showResults = true,
    customTitle,
}) => {
    const unitSystem = projectData?.unit || 'Metric';
    const projectType = projectData?.type || 'container_yard';
    const isParkingDeck = projectType === 'parking_deck' || projectType === 'parkingdeck';
    // Prioritize top-level method from projectData (Redux state), fallback to projectInfo.data.method
    const projectMethod = projectData?.method;

    // Helper function to get unit based on system
    const getUnit = (metricUnit: string, imperialUnit: string) => {
        return unitSystem === 'Metric' ? metricUnit : imperialUnit;
    };

    // Process deckDesign data for unit conversion
    const processedDeckDesign = useMemo(() => {
        if (!output?.deckDesign) return null;

        // Deep clone to avoid mutating the original output object
        const dd = JSON.parse(JSON.stringify(output.deckDesign));

        if (unitSystem === unitEnum.Imperial) {
            // API is assumed to provide data in SI units (Metric)
            // Convert relevant fields to Imperial units
            dd.unreinforcedHeight =
                convertFieldValue(
                    { name: 'deckDesign.unreinforcedHeight', type: 'number', unit: ['m', 'ft'], isConvertible: true },
                    dd.unreinforcedHeight,
                    unitEnum.SI, // Source unit system
                    unitEnum.Imperial, // Target unit system
                    'pdf.deckDesign.unreinforcedHeight' // Optional context for logging
                ) ?? dd.unreinforcedHeight;

            dd.reinforcedHeight =
                convertFieldValue(
                    { name: 'deckDesign.reinforcedHeight', type: 'number', unit: ['m', 'ft'], isConvertible: true },
                    dd.reinforcedHeight,
                    unitEnum.SI,
                    unitEnum.Imperial,
                    'pdf.deckDesign.reinforcedHeight'
                ) ?? dd.reinforcedHeight;

            dd.thicknessSaving =
                convertFieldValue(
                    { name: 'deckDesign.thicknessSaving', type: 'number', unit: ['m', 'ft'], isConvertible: true },
                    dd.thicknessSaving,
                    unitEnum.SI,
                    unitEnum.Imperial,
                    'pdf.deckDesign.thicknessSaving'
                ) ?? dd.thicknessSaving;
            // thicknessSavingPercentage is unitless and does not require conversion.
        }
        return dd;
    }, [output?.deckDesign, unitSystem]);

    // Process results data
    const resultsTable = useMemo(() => {
        if (!processedDeckDesign) return [];
        return [
            [t('unstabilized_deck'), 'hᵤ', processedDeckDesign.unreinforcedHeight?.toFixed(2) || 'N/A', getUnit('m', 'ft')],
            [t('stabilized_deck'), 'hₛ', processedDeckDesign.reinforcedHeight?.toFixed(2) || 'N/A', getUnit('m', 'ft')],
            [t('thickness_saving'), 'Δh', processedDeckDesign.thicknessSaving?.toFixed(2) || 'N/A', getUnit('m', 'ft')],
            [
                t('thickness_saving_percentage'),
                'Δh%', // Using a distinct symbol for percentage as Δh is used for absolute saving
                processedDeckDesign.thicknessSavingPercentage?.toFixed(0) || 'N/A',
                '%',
            ],
        ];
    }, [processedDeckDesign, t, getUnit]);

    // Helper for consistent conversion and rounding for input tables
    const convertInputValue = (value: number | undefined, fieldName: string, metricUnit: string, imperialUnit: string): string => {
        if (typeof value !== 'number') return 'N/A';
        if (unitSystem === unitEnum.Imperial) {
            // Use convertFieldValue for conversion and rounding
            const converted = convertFieldValue({ name: fieldName, type: 'number', unit: [metricUnit, imperialUnit], isConvertible: true }, value, unitEnum.SI, unitEnum.Imperial, `pdf.${fieldName}`);
            return converted !== null ? converted.toFixed(3) : value.toString();
        }
        return value.toString();
    };

    // Input data tables based on projectMethod and projectType
    let inputLoadTableData: string[][] = [];
    let inputSubgradeTableData: string[][] = [];
    let inputEmbarkmentTableData: string[][] = [];

    const projectInputs = projectData?.projectInfo?.data;

    // Use robust method resolution with fallback for consistency with other PDF components
    const currentProjectMethod = (projectMethod as string) || (projectInputs?.method as string) || 'bcr';

    if (projectInputs && currentProjectMethod === 'bcr') {
        // 🔧 FIX: Access FLAT structure directly (PD3/PD4)
        // Data is at top level after transformProjectFromBackend()
        const fsDesignValue = (projectInputs as any)?.fsDesign?.fsDesign?.toString() || 'N/A';

        // Load Data for BCR - access directly from projectInputs (FLAT structure)
        if (projectType === 'container_yard' || projectType === 'containeryard') {
            const cyBcrLoad = (projectInputs as any)?.load;
            inputLoadTableData = [
                [t('deckWidth'), 'B', convertInputValue(cyBcrLoad?.deckWidth, 'deckWidth', 'm', 'ft'), getUnit('m', 'ft')],
                [t('deckLength'), 'L', convertInputValue(cyBcrLoad?.deckLength, 'deckLength', 'm', 'ft'), getUnit('m', 'ft')],
                [t('uniformLoadRes'), 'q', convertInputValue(cyBcrLoad?.uniformLoad, 'uniformLoad', 'kPa', 'psf'), getUnit('kPa', 'psf')],
                [t('safetyFactor'), 'SF', fsDesignValue, '-'],
            ];
        } else if (projectType === 'parking_deck' || projectType === 'parkingdeck') {
            const pdBcrLoad = (projectInputs as any)?.load;
            inputLoadTableData = [
                [t('wheel_load'), 'P', convertInputValue(pdBcrLoad?.wheelLoad, 'wheelLoad', 'kN', 'lbf'), getUnit('kN', 'lbf')],
                [t('tire_inflation_pressure'), 'p', convertInputValue(pdBcrLoad?.tireInflationPressure, 'tireInflationPressure', 'kPa', 'psi'), getUnit('kPa', 'psi')],
                [t('safetyFactor'), 'SF', fsDesignValue, '-'],
            ];
        }

        // Subgrade Data for BCR - access directly from projectInputs (FLAT structure)
        const bcrSubgrade = (projectInputs as any)?.subgradeCharacteristics;
        inputSubgradeTableData = [
            [t('unit_weight', { unit_measure: '' }), 'γ', convertInputValue(bcrSubgrade?.unitWeight, 'unitWeight', 'kN/m³', 'pcf'), getUnit('kN/m³', 'pcf')],
            [t('friction_angle', { unit_measure: '' }), 'φ', convertInputValue(bcrSubgrade?.frictionAngle, 'frictionAngle', '°', '°'), '°'],
            [t('cohesion', { unit_measure: '' }), 'c', convertInputValue(bcrSubgrade?.cohesion, 'cohesion', 'kPa', 'psf'), getUnit('kPa', 'psf')],
        ];

        // Embarkment Data for BCR - access directly from projectInputs (FLAT structure)
        const bcrEmbarkment = (projectInputs as any)?.embankmentCharacteristics;
        inputEmbarkmentTableData = [
            [t('embarkment_weight', { unit_measure: '' }), 'γₑ', convertInputValue(bcrEmbarkment?.unitWeight, 'embarkmentUnitWeight', 'kN/m³', 'pcf'), getUnit('kN/m³', 'pcf')],
            [t('embarkment_friction_angle', { unit_measure: '' }), 'φₑ', convertInputValue(bcrEmbarkment?.frictionAngle, 'embarkmentFrictionAngle', '°', '°'), '°'],
            [t('embarkment_cohesion', { unit_measure: '' }), 'cₑ', convertInputValue(bcrEmbarkment?.cohesion, 'embarkmentCohesion', 'kPa', 'psf'), getUnit('kPa', 'psf')],
            [t('loadSpreadingAngleRes', { unit_measure: '' }), 'αᵤ', convertInputValue(bcrEmbarkment?.loadSpreadingAngleU, 'loadSpreadingAngleU', '°', '°'), '°'],
            [t('loadSpreadingAngleRes', { unit_measure: '' }), 'αᵣ', convertInputValue(bcrEmbarkment?.loadSpreadingAngleR, 'loadSpreadingAngleR', '°', '°'), '°'],
        ];
    } else if (projectInputs && currentProjectMethod === 'static') {
        // 🔧 FIX: Access FLAT structure directly (PD3/PD4)
        // Build separate Load and Subgrade tables like BCR method
        const staticLoadData = (projectInputs as any)?.load;
        const staticSubgradeData = (projectInputs as any)?.subgrade;
        const fsDesignValueStatic = (projectInputs as any)?.fsDesign?.fsDesign?.toString() || 'N/A';

        // Load table data (includes Safety Factor)
        if (projectType === 'container_yard' || projectType === 'containeryard') {
            inputLoadTableData = [
                [t('static_deck_half_width'), "B'", convertInputValue(staticLoadData?.deckHalfWidth, 'staticDeckHalfWidth', 'm', 'ft'), getUnit('m', 'ft')],
                [t('static_deck_half_length'), "L'", convertInputValue(staticLoadData?.deckHalfLength, 'staticDeckHalfLength', 'm', 'ft'), getUnit('m', 'ft')],
                [t('static_uniform_load'), 'q', convertInputValue(staticLoadData?.uniformLoad, 'staticUniformLoad', 'kPa', 'psf'), getUnit('kPa', 'psf')],
                [t('static_safety_factor'), 'FS', fsDesignValueStatic, '-'],
            ];
        } else if (projectType === 'parking_deck' || projectType === 'parkingdeck') {
            inputLoadTableData = [
                [t('wheel_load'), 'P', convertInputValue(staticLoadData?.wheelLoad, 'staticWheelLoad', 'kN', 'lbf'), getUnit('kN', 'lbf')],
                [t('tire_inflation_pressure'), 'p', convertInputValue(staticLoadData?.tireInflationPressure, 'staticTireInflationPressure', 'kPa', 'psi'), getUnit('kPa', 'psi')],
                [t('static_safety_factor'), 'FS', fsDesignValueStatic, '-'],
            ];
        }

        // Subgrade table data
        if (staticSubgradeData?.cbrSubgrade !== undefined && staticSubgradeData.cbrSubgrade !== null) {
            inputSubgradeTableData = [[t('static_cbr'), 'CBR', convertInputValue(staticSubgradeData.cbrSubgrade, 'staticCbrSubgrade', '%', '%'), '%']];
        } else if (staticSubgradeData?.deformationModulus !== undefined && staticSubgradeData.deformationModulus !== null) {
            inputSubgradeTableData = [[t('static_deformation_modulus'), 'E', convertInputValue(staticSubgradeData.deformationModulus, 'staticDeformationModulus', 'MPa', 'psi'), getUnit('MPa', 'psi')]];
        }
    }

    // Determine if there is any input data to display
    const hasBcrInputData = currentProjectMethod === 'bcr' && (inputLoadTableData.length > 0 || inputSubgradeTableData.length > 0 || inputEmbarkmentTableData.length > 0);
    const hasStaticInputData = currentProjectMethod === 'static' && (inputLoadTableData.length > 0 || inputSubgradeTableData.length > 0);
    const showInputDataSummary = showInputData && (hasBcrInputData || hasStaticInputData);
    const projectInfoDescriptionKey = isParkingDeck
        ? currentProjectMethod === 'static'
            ? 'parking_deck_static_project_info_desc_pdf'
            : 'parking_deck_bcr_project_info_desc_pdf'
        : currentProjectMethod === 'static'
          ? 'container_yard_static_project_info_desc_pdf'
          : 'container_yard_bcr_project_info_desc_pdf';
    const projectInfoDescription = t(projectInfoDescriptionKey) || 'Project Information';

    return (
        <>
            {/* GeoGrid Project Header */}
            <View style={pdfSharedStyles.spacer1} />
            <Text style={[pdfSharedStyles.projectInfoProjectType]}>
                {`GROUNDMAC - ${isParkingDeck ? t('project_type_parking_deck') || 'Parking Deck' : t('project_type_container_yard') || 'Container Yard'}`}
            </Text>
            <View style={pdfSharedStyles.spacer0} />
            <Text>{parseHtmlToPdfText(projectInfoDescription, pdfSharedStyles.projectInfoSubtitle)}</Text>

            {/* Input Data Summary Section */}
            {showInputDataSummary && (
                <View style={pdfSharedStyles.section}>
                    <View style={pdfSharedStyles.spacer1} />
                    <Text style={[pdfSharedStyles.title, { marginTop: 8, marginBottom: 0, textAlign: 'left' }]}>{t('input_data_summary')}</Text>
                    <View style={pdfSharedStyles.spacer0} />
                    {currentProjectMethod === 'bcr' && (
                        <View style={pdfSharedStyles.innSection}>
                            {/* Load and Subgrade Tables side by side */}
                            {inputLoadTableData.length > 0 && (
                                <View style={pdfSharedStyles.colHalf}>
                                    <Text style={pdfSharedStyles.tableTitle}>{t('load')}</Text>
                                    <View style={pdfSharedStyles.spacer0} />
                                    <View style={pdfSharedStyles.table}>
                                        <View style={pdfSharedStyles.tableRow}>
                                            <View style={[pdfSharedStyles.tableColHeader, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCellHeader, pdfSharedStyles.leftCell]}>{t('output_description')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColHeaderSymbol}>
                                                <Text style={pdfSharedStyles.tableCellHeader}>{t('output_symbol')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColHeaderValue}>
                                                <Text style={pdfSharedStyles.tableCellHeader}>{t('output_value')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColHeaderUnit}>
                                                <Text style={pdfSharedStyles.tableCellHeader}>{t('output_unit')}</Text>
                                            </View>
                                        </View>
                                        {inputLoadTableData.map((row, idx) => (
                                            <View style={[pdfSharedStyles.tableRow, idx % 2 === 0 ? pdfSharedStyles.evenRow : pdfSharedStyles.oddRow]} key={`load-${idx}`}>
                                                <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                    <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{row[0]}</Text>
                                                </View>
                                                <View style={pdfSharedStyles.tableColSymbol}>
                                                    <Text style={pdfSharedStyles.tableCell}>{row[1]}</Text>
                                                </View>
                                                <ValueUnitCells value={row[2]} unit={row[3]} />
                                            </View>
                                        ))}
                                    </View>
                                </View>
                            )}

                            {/* Subgrade Table */}
                            {inputSubgradeTableData.length > 0 && (
                                <View style={pdfSharedStyles.colHalf}>
                                    <Text style={pdfSharedStyles.tableTitle}>{t('subgrade')}</Text>
                                    <View style={pdfSharedStyles.spacer0} />
                                    <View style={pdfSharedStyles.table}>
                                        <View style={pdfSharedStyles.tableRow}>
                                            <View style={[pdfSharedStyles.tableColHeader, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCellHeader, pdfSharedStyles.leftCell]}>{t('output_description')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColHeaderSymbol}>
                                                <Text style={pdfSharedStyles.tableCellHeader}>{t('output_symbol')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColHeaderValue}>
                                                <Text style={pdfSharedStyles.tableCellHeader}>{t('output_value')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColHeaderUnit}>
                                                <Text style={pdfSharedStyles.tableCellHeader}>{t('output_unit')}</Text>
                                            </View>
                                        </View>
                                        {inputSubgradeTableData.map((row, idx) => (
                                            <View style={[pdfSharedStyles.tableRow, idx % 2 === 0 ? pdfSharedStyles.evenRow : pdfSharedStyles.oddRow]} key={`subgrade-${idx}`}>
                                                <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                    <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{row[0]}</Text>
                                                </View>
                                                <View style={pdfSharedStyles.tableColSymbol}>
                                                    <Text style={pdfSharedStyles.tableCell}>{row[1]}</Text>
                                                </View>
                                                <ValueUnitCells value={row[2]} unit={row[3]} />
                                            </View>
                                        ))}
                                    </View>
                                </View>
                            )}

                            {/* Embarkment Fill Table */}
                            {inputEmbarkmentTableData.length > 0 && (
                                <View style={pdfSharedStyles.colHalf}>
                                    <Text style={pdfSharedStyles.tableTitle}>{t('embarkment_fill')}</Text>
                                    <View style={pdfSharedStyles.spacer0} />
                                    <View style={pdfSharedStyles.table}>
                                        <View style={pdfSharedStyles.tableRow}>
                                            <View style={[pdfSharedStyles.tableColHeader, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCellHeader, pdfSharedStyles.leftCell]}>{t('output_description')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColHeaderSymbol}>
                                                <Text style={pdfSharedStyles.tableCellHeader}>{t('output_symbol')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColHeaderValue}>
                                                <Text style={pdfSharedStyles.tableCellHeader}>{t('output_value')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColHeaderUnit}>
                                                <Text style={pdfSharedStyles.tableCellHeader}>{t('output_unit')}</Text>
                                            </View>
                                        </View>
                                        {inputEmbarkmentTableData.map((row, idx) => (
                                            <View style={[pdfSharedStyles.tableRow, idx % 2 === 0 ? pdfSharedStyles.evenRow : pdfSharedStyles.oddRow]} key={`embarkment-${idx}`}>
                                                <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                    <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{row[0]}</Text>
                                                </View>
                                                <View style={pdfSharedStyles.tableColSymbol}>
                                                    <Text style={pdfSharedStyles.tableCell}>{row[1]}</Text>
                                                </View>
                                                <ValueUnitCells value={row[2]} unit={row[3]} />
                                            </View>
                                        ))}
                                    </View>
                                </View>
                            )}
                        </View>
                    )}
                    {currentProjectMethod === 'static' && (inputLoadTableData.length > 0 || inputSubgradeTableData.length > 0) && (
                        <View style={pdfSharedStyles.innSection}>
                            {/* Load Table */}
                            {inputLoadTableData.length > 0 && (
                                <View style={pdfSharedStyles.colHalf}>
                                    <Text style={pdfSharedStyles.tableTitle}>{t('load')}</Text>
                                    <View style={pdfSharedStyles.spacer0} />
                                    <View style={pdfSharedStyles.table}>
                                        <View style={pdfSharedStyles.tableRow}>
                                            <View style={[pdfSharedStyles.tableColHeader, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCellHeader, pdfSharedStyles.leftCell]}>{t('output_description')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColHeaderSymbol}>
                                                <Text style={pdfSharedStyles.tableCellHeader}>{t('output_symbol')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColHeaderValue}>
                                                <Text style={pdfSharedStyles.tableCellHeader}>{t('output_value')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColHeaderUnit}>
                                                <Text style={pdfSharedStyles.tableCellHeader}>{t('output_unit')}</Text>
                                            </View>
                                        </View>
                                        {inputLoadTableData.map((row, idx) => (
                                            <View style={[pdfSharedStyles.tableRow, idx % 2 === 0 ? pdfSharedStyles.evenRow : pdfSharedStyles.oddRow]} key={`static-load-${idx}`}>
                                                <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                    <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{row[0]}</Text>
                                                </View>
                                                <View style={pdfSharedStyles.tableColSymbol}>
                                                    <Text style={pdfSharedStyles.tableCell}>{row[1]}</Text>
                                                </View>
                                                <ValueUnitCells value={row[2]} unit={row[3]} />
                                            </View>
                                        ))}
                                    </View>
                                </View>
                            )}

                            {/* Subgrade Table */}
                            {inputSubgradeTableData.length > 0 && (
                                <View style={pdfSharedStyles.colHalf}>
                                    <Text style={pdfSharedStyles.tableTitle}>{t('subgrade')}</Text>
                                    <View style={pdfSharedStyles.spacer0} />
                                    <View style={pdfSharedStyles.table}>
                                        <View style={pdfSharedStyles.tableRow}>
                                            <View style={[pdfSharedStyles.tableColHeader, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCellHeader, pdfSharedStyles.leftCell]}>{t('output_description')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColHeaderSymbol}>
                                                <Text style={pdfSharedStyles.tableCellHeader}>{t('output_symbol')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColHeaderValue}>
                                                <Text style={pdfSharedStyles.tableCellHeader}>{t('output_value')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColHeaderUnit}>
                                                <Text style={pdfSharedStyles.tableCellHeader}>{t('output_unit')}</Text>
                                            </View>
                                        </View>
                                        {inputSubgradeTableData.map((row, idx) => (
                                            <View style={[pdfSharedStyles.tableRow, idx % 2 === 0 ? pdfSharedStyles.evenRow : pdfSharedStyles.oddRow]} key={`static-subgrade-${idx}`}>
                                                <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                    <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{row[0]}</Text>
                                                </View>
                                                <View style={pdfSharedStyles.tableColSymbol}>
                                                    <Text style={pdfSharedStyles.tableCell}>{row[1]}</Text>
                                                </View>
                                                <ValueUnitCells value={row[2]} unit={row[3]} />
                                            </View>
                                        ))}
                                    </View>
                                </View>
                            )}
                        </View>
                    )}
                    {/* Chart for Static method - placed after tables */}
                    {currentProjectMethod === 'static' && chartImage && showChart && (
                        <View style={pdfSharedStyles.graphicSection} wrap={false}>
                            <View style={pdfSharedStyles.spacer1} />
                            <Text style={pdfSharedStyles.subTitle}>{t('factors_of_safety')}</Text>
                            <Image style={[{ width: '90%', height: 'auto' }]} src={chartImage} />
                        </View>
                    )}
                </View>
            )}

            {/* <View break /> */}

            {/* Chart for BCR method */}
            {chartImage && currentProjectMethod !== 'static' && showChart && (
                <View style={pdfSharedStyles.graphicSection} wrap={false}>
                    <View style={pdfSharedStyles.spacer2} />
                    <Text style={pdfSharedStyles.subTitle}>{t('factors_of_safety')}</Text>
                    <Image style={[{ width: '100%', height: 'auto' }]} src={chartImage} />
                </View>
            )}

            {/* Results Section */}
            {showResults && (
                <>
                    <View style={pdfSharedStyles.spacer1} />
                    <Text style={[pdfSharedStyles.title, pdfSharedStyles.resultTitle]}>{t('results') || 'Results'}</Text>
                    <View style={pdfSharedStyles.spacer1} />

                    {/* Tire Images */}
                    {(tireImageUnreinforced || tireImageReinforced) && (
                        <View style={pdfSharedStyles.graphicImagesContainer}>
                            <View style={{ alignItems: 'center', width: '50%', marginLeft: '0' }}>
                                {tireImageUnreinforced ? (
                                    <Image key={tireImageUnreinforced.slice(0, 32)} style={pdfSharedStyles.graphicImage} src={tireImageUnreinforced} />
                                ) : (
                                    <Text style={pdfSharedStyles.graphicDescription}>Unreinforced image not available</Text>
                                )}
                                <Text style={pdfSharedStyles.graphicDescription}>{t('left_unstabilized')}</Text>
                            </View>
                            <View style={{ alignItems: 'center', width: '50%', marginLeft: '0' }}>
                                {tireImageReinforced ? (
                                    <Image key={tireImageReinforced.slice(0, 32)} style={pdfSharedStyles.graphicImage} src={tireImageReinforced} />
                                ) : (
                                    <Text style={pdfSharedStyles.graphicDescription}>Reinforced image not available</Text>
                                )}
                                <Text style={pdfSharedStyles.graphicDescription}>{t('right_stabilized')}</Text>
                            </View>
                        </View>
                    )}

                    <View style={pdfSharedStyles.spacer3} />

                    {/* Results Table */}
                    <View style={pdfSharedStyles.section}>
                        {resultsTable.length > 0 && (
                            <View style={pdfSharedStyles.table}>
                                <View style={pdfSharedStyles.tableRow}>
                                    <View style={[pdfSharedStyles.tableColHeader, pdfSharedStyles.dimFirstCol]}>
                                        <Text style={[pdfSharedStyles.tableCellHeader, pdfSharedStyles.leftCell]}>{t('output_description')}</Text>
                                    </View>
                                    <View style={pdfSharedStyles.tableColHeaderSymbol}>
                                        <Text style={pdfSharedStyles.tableCellHeader}>{t('output_symbol')}</Text>
                                    </View>
                                    <View style={pdfSharedStyles.tableColHeaderValue}>
                                        <Text style={pdfSharedStyles.tableCellHeader}>{t('output_value')}</Text>
                                    </View>
                                    <View style={pdfSharedStyles.tableColHeaderUnit}>
                                        <Text style={pdfSharedStyles.tableCellHeader}>{t('output_unit')}</Text>
                                    </View>
                                </View>
                                {resultsTable.map((row, index) => (
                                    <View key={index} style={[pdfSharedStyles.tableRow, index % 2 === 0 ? pdfSharedStyles.evenRow : pdfSharedStyles.oddRow]}>
                                        <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                            <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{row[0]}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColSymbol}>
                                            <Text style={pdfSharedStyles.tableCell}>{row[1]}</Text>
                                        </View>
                                        <ValueUnitCells value={row[2]} unit={row[3]} />
                                    </View>
                                ))}
                            </View>
                        )}
                    </View>
                </>
            )}
        </>
    );
};
